export type MendelowMode = "focal" | "system";

export type MendelowWeights = {
  power: {
    inDegree: number;
    avgWorkWellIn: number;
  };
  interest: {
    outDegree: number;
    avgWorkWellOut: number;
    categoryBreadthOut: number;
  };
};

export type MendelowThreshold = {
  method: "median" | "quantile";
  quantile: number;
};

export type MendelowConfig = {
  mode: MendelowMode;
  focalOrgId?: string | null;
  workWellField?: string;
  categoriesField?: string;
  weights: MendelowWeights;
  threshold: MendelowThreshold;
};

export type MendelowMetrics = {
  orgId: string;
  inDegree: number;
  outDegree: number;
  avgWorkWellIn: number;
  avgWorkWellOut: number;
  categoryBreadthOut: number;
};

export type MendelowResult = MendelowMetrics & {
  powerScore: number;
  interestScore: number;
  powerNorm: number;
  interestNorm: number;
  quadrant: "Minimal effort" | "Keep informed" | "Keep satisfied" | "Key players";
};

export type MendelowEdge = {
  a: string;
  b: string;
  workWell?: number | null;
  categories?: string[];
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const avg = (vals: number[]) => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0);

const normalizeMinMax = (vals: number[]) => {
  if (!vals.length) return vals.map(() => 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  if (max === min) return vals.map(() => 0);
  return vals.map((v) => (v - min) / (max - min));
};

const quantile = (vals: number[], q: number) => {
  if (!vals.length) return 0;
  const sorted = [...vals].sort((a, b) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
};

// Scoring rationale:
// - Power uses incoming nominations and how positively the org is rated by others.
// - Interest uses outgoing nominations and how positively the org rates others + breadth of collaboration.
// In focal mode, only edges involving the focal org are considered:
// - Power: org -> focal (how much the org reaches toward the focal org).
// - Interest: focal -> org (how much the focal org reaches toward the org).
export function scoreMendelow(
  orgIds: string[],
  edges: MendelowEdge[],
  config: MendelowConfig
): MendelowResult[] {
  const focal = config.mode === "focal" ? config.focalOrgId ?? null : null;

  const metrics = orgIds.map<MendelowMetrics>((orgId) => {
    const outgoing = edges.filter((e) => e.a === orgId);
    const incoming = edges.filter((e) => e.b === orgId);

    let scopedOutgoing = outgoing;
    let scopedIncoming = incoming;

    if (config.mode === "focal" && focal) {
      scopedOutgoing = edges.filter((e) => e.a === focal && e.b === orgId);
      scopedIncoming = edges.filter((e) => e.a === orgId && e.b === focal);
    }

    const outWork = scopedOutgoing.map((e) => e.workWell ?? 0).filter((v) => Number.isFinite(v));
    const inWork = scopedIncoming.map((e) => e.workWell ?? 0).filter((v) => Number.isFinite(v));

    const categorySet = new Set<string>();
    scopedOutgoing.forEach((e) => (e.categories ?? []).forEach((c) => categorySet.add(c)));

    return {
      orgId,
      inDegree: scopedIncoming.length,
      outDegree: scopedOutgoing.length,
      avgWorkWellIn: avg(inWork),
      avgWorkWellOut: avg(outWork),
      categoryBreadthOut: categorySet.size,
    };
  });

  const inDegs = metrics.map((m) => m.inDegree);
  const outDegs = metrics.map((m) => m.outDegree);
  const avgIn = metrics.map((m) => m.avgWorkWellIn);
  const avgOut = metrics.map((m) => m.avgWorkWellOut);
  const catBreadth = metrics.map((m) => m.categoryBreadthOut);

  const nInDegs = normalizeMinMax(inDegs);
  const nOutDegs = normalizeMinMax(outDegs);
  const nAvgIn = normalizeMinMax(avgIn);
  const nAvgOut = normalizeMinMax(avgOut);
  const nCat = normalizeMinMax(catBreadth);

  const powerScores = metrics.map((m, idx) => {
    const score =
      config.weights.power.inDegree * nInDegs[idx] +
      config.weights.power.avgWorkWellIn * nAvgIn[idx];
    return clamp01(score);
  });

  const interestScores = metrics.map((m, idx) => {
    const score =
      config.weights.interest.outDegree * nOutDegs[idx] +
      config.weights.interest.avgWorkWellOut * nAvgOut[idx] +
      config.weights.interest.categoryBreadthOut * nCat[idx];
    return clamp01(score);
  });

  const powerThreshold =
    config.threshold.method === "median"
      ? quantile(powerScores, 0.5)
      : quantile(powerScores, config.threshold.quantile);
  const interestThreshold =
    config.threshold.method === "median"
      ? quantile(interestScores, 0.5)
      : quantile(interestScores, config.threshold.quantile);

  return metrics.map((m, idx) => {
    const powerScore = powerScores[idx];
    const interestScore = interestScores[idx];
    const highPower = powerScore >= powerThreshold;
    const highInterest = interestScore >= interestThreshold;
    let quadrant: MendelowResult["quadrant"] = "Minimal effort";
    if (highPower && highInterest) quadrant = "Key players";
    else if (highPower && !highInterest) quadrant = "Keep satisfied";
    else if (!highPower && highInterest) quadrant = "Keep informed";
    return {
      ...m,
      powerScore,
      interestScore,
      powerNorm: powerScore,
      interestNorm: interestScore,
      quadrant,
    };
  });
}
